// Phase 1A Step 32 — useRolloverChain hook.
//
// Read-only access to the authenticated user's Phantom rollover chain.
// Phase 1A allows exactly one phantom chain per user (created on first
// Phantom slip via Migration 0024's create_phantom_simulation_cycle RPC).
// We still .order(created_at) + .limit(1) defensively in case future
// phases lift that constraint.
//
// Trust contract:
//   - RLS limits SELECT to the caller's own rollover_chains rows
//     (rollover_chains_select_own policy from Migration 0011).
//   - Direct INSERT/UPDATE/DELETE are blocked at the DB level
//     (Migration 0024 dropped the matching policies). The hook never
//     attempts writes.
//   - No RPC calls. No Supabase service role. No external fetch.
//   - No localStorage / sessionStorage custom usage.
//   - No randomness. No Kelly logic. No protected-amount logic. No
//     real-stake recommendations. No fake metrics.
//
// Returns chain=null when the user has no chain yet (e.g. opted-out
// users or anyone who has not created a Phantom slip). That is not an
// error — it is the truthful "empty" state the Vault hero block will
// render with explanatory copy.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import type { Database } from "../db/types.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export type RolloverChainRow = Database["public"]["Tables"]["rollover_chains"]["Row"];

export interface RolloverChainState {
  chain: RolloverChainRow | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useRolloverChain(): RolloverChainState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [chain, setChain] = useState<RolloverChainRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setChain(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from("rollover_chains")
      .select("*")
      .eq("user_id", user.id)
      .eq("mode", "phantom")
      .order("created_at", { ascending: false })
      .limit(1);
    if (fetchError) {
      setChain(null);
      setError(fetchError.message);
    } else {
      setChain((data?.[0] as RolloverChainRow | undefined) ?? null);
    }
    setLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<RolloverChainState>(
    () => ({ chain, loading, error, refetch }),
    [chain, loading, error, refetch],
  );
}
